import React, { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Wallet, 
  Briefcase, 
  ClipboardList, 
  Receipt, 
  Users,
  LogOut,
  MessageCircle,
  Bot,
  AlertTriangle,
  Calendar,
  X,
  FileText,
  Shield,
  Headphones,
  Activity
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { filterNavSections } from '../utils/financePermissions';

const drawerSections = [
  {
    title: 'Core',
    items: [
      { path: '/command-centre', icon: LayoutDashboard, label: 'Command Centre' },
      { path: '/calendar', icon: Calendar, label: 'Calendar' },
      { path: '/communications', icon: MessageCircle, label: 'Communications' },
    ]
  },
  {
    title: 'Work',
    items: [
      { path: '/jobs', icon: Briefcase, label: 'Job Life' },
      { path: '/quotes', icon: ClipboardList, label: 'Quote Life' },
      { path: '/invoices', icon: Receipt, label: 'Invoice Life' },
    ]
  },
  {
    title: 'Finance',
    items: [
      { path: '/finance', icon: Wallet, label: 'Finance Life' },
      { path: '/business-reality', icon: Activity, label: 'Business Reality' },
    ]
  },
  {
    title: 'Assistant',
    items: [
      { path: '/assistant-life', icon: Bot, label: 'Assistant-Life' },
      { path: '/emergency', icon: AlertTriangle, label: 'Emergency Mode', emergency: true },
    ]
  },
  {
    title: 'Portal',
    items: [
      { path: '/client', icon: Users, label: 'Client Portal' },
    ]
  },
  {
    title: 'Legal & Trust',
    items: [
      { path: '/legal/terms', icon: FileText, label: 'Terms of Service' },
      { path: '/legal/privacy', icon: Shield, label: 'Privacy Policy' },
      { path: '/legal/ai-use', icon: Bot, label: 'AI Use & Limitations' },
      { path: '/legal/emergency-disclaimer', icon: AlertTriangle, label: 'Emergency Disclaimer' },
    ]
  },
  {
    title: 'Support',
    items: [
      { path: '/support', icon: Headphones, label: 'Support & Legal' },
    ]
  },
];

export function MobileDrawer({ isOpen, onClose }) {
  const location = useLocation();
  const { user, logout, isFounder } = useAuth();

  const filteredSections = filterNavSections(drawerSections, user);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on route change
  useEffect(() => {
    onClose();
    // eslint-disable-next-line
  }, [location.pathname]);

  const handleLogout = async () => {
    onClose();
    await logout();
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getUserRole = () => {
    if (isFounder && isFounder()) return 'Founder';
    if (user?.role === 'MANAGER') return 'Manager';
    return 'User';
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="mobile-drawer-backdrop"
          onClick={onClose}
          data-testid="mobile-drawer-backdrop"
        />
      )}

      {/* Drawer */}
      <aside 
        className={`mobile-drawer ${isOpen ? 'open' : ''}`}
        data-testid="mobile-drawer"
      >
        {/* Drawer Header */}
        <div className="mobile-drawer-header">
          <div className="mobile-drawer-logo">
            <span className="logo-trade">Trade</span>
            <span className="logo-life">Life</span>
          </div>
          <button 
            className="mobile-drawer-close"
            onClick={onClose}
            aria-label="Close menu"
            data-testid="mobile-drawer-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* User Info */}
        <div className="mobile-drawer-user">
          <div className="user-avatar">{getInitials(user?.name)}</div>
          <div className="mobile-drawer-user-info">
            <div className="user-name">{user?.name || 'User'}</div>
            <div className="user-role">{getUserRole()}</div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="mobile-drawer-nav">
          {filteredSections.map((section) => (
            <div key={section.title} className="mobile-drawer-section">
              <div className="mobile-drawer-section-title">{section.title}</div>
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `mobile-drawer-link ${isActive ? 'active' : ''}`}
                  onClick={onClose}
                  style={item.emergency ? { color: 'var(--status-danger)' } : {}}
                  data-testid={`drawer-link-${item.path.replace(/\//g, '-').slice(1)}`}
                >
                  <item.icon size={18} className="mobile-drawer-link-icon" style={item.emergency ? { color: 'var(--status-danger)' } : {}} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Logout */}
        <div className="mobile-drawer-footer">
          <button 
            className="mobile-drawer-logout"
            onClick={handleLogout}
            data-testid="mobile-drawer-logout-btn"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}

export default MobileDrawer;
